import api from 'api/instance';
import {
  PURCHASE_ORDER_SEND_BCC_EMAILS,
  PURCHASE_ORDER_SEND_CC_EMAILS,
} from 'constants/purchaseOrder';
import { FetchQueryType, FetchResponse } from 'types/common';
import { PurchaseOrder } from 'types/purchaseOrder';
import {
  CreatePurchaseOrderApiPayload,
  DeletePurchaseOrderPayload,
  PayPurchaseOrderPayload,
  ReceivePurchaseOrderPayload,
  SendPurchaseOrderPayload,
  UpdatePurchaseOrderCostsPayload,
} from 'types/purchaseOrder';

const URL = `${process.env.REACT_APP_API_URL}`;

export type FetchPurchaseOrdersParams = Omit<FetchQueryType, 'status'> & {
  washHub?: string;
  search?: string;
  status?: string | string[];
  excludeStatus?: string | string[];
  manufacturerId?: string | string[];
  destinationLocationId?: string | string[];
  skuId?: string | string[];
  sortByField?: string;
  sort?: number;
  tab?: 'all' | 'active' | 'completed';
};

export const fetchPurchaseOrders = async (
  params: FetchPurchaseOrdersParams,
) => {
  const { data } = await api.get<FetchResponse<PurchaseOrder>>(
    `${URL}/purchase-order/list`,
    { params },
  );
  return data;
};

export const getPurchaseOrderCounts = async (
  params: Omit<
    FetchPurchaseOrdersParams,
    | 'page'
    | 'limit'
    | 'tab'
    | 'sort'
    | 'sortByField'
    | 'excludeStatus'
    | 'status'
  > & {
    status?: string | string[];
    manufacturerId?: string | string[];
    destinationLocationId?: string | string[];
    skuId?: string | string[];
  } = {},
) => {
  const { data } = await api.get<{
    message: string;
    item: {
      all: number;
      active: number;
      completed: number;
    };
  }>(`${URL}/purchase-order/counts`, { params });

  return data.item;
};

export const createPurchaseOrderWithAttachment = async (
  file: Blob,
  fileName: string,
  payload: CreatePurchaseOrderApiPayload,
) => {
  const formData = new FormData();
  formData.append('file', file, fileName);
  formData.append('data', JSON.stringify(payload));

  const { data } = await api.post(
    `${URL}/purchase-order/create-with-attachment`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );

  return data;
};

export const addPurchaseOrderAttachment = async (
  purchaseOrderId: string,
  file: File,
) => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const { data } = await api.post<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

export const fetchPurchaseOrderAttachmentUrl = async (
  purchaseOrderId: string,
  key: string,
) => {
  const { data } = await api.get<{
    message: string;
    item: { url: string };
  }>(`${URL}/purchase-order/${purchaseOrderId}/attachment-url`, {
    params: { key },
  });

  return data.item.url;
};

export const deletePurchaseOrderAttachment = async (
  purchaseOrderId: string,
  key: string,
) => {
  const { data } = await api.delete<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/attachment`, {
    params: { key },
  });

  return data;
};

export const updatePurchaseOrderWithAttachment = async (
  purchaseOrderId: string,
  file: Blob,
  fileName: string,
  payload: CreatePurchaseOrderApiPayload,
) => {
  const formData = new FormData();
  formData.append('file', file, fileName);
  formData.append('data', JSON.stringify(payload));

  const { data } = await api.post<{
    message: string;
    item: PurchaseOrder;
  }>(
    `${URL}/purchase-order/${purchaseOrderId}/update-with-attachment`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );

  return data;
};

export const sendPurchaseOrder = async (
  purchaseOrderId: string,
  payload: SendPurchaseOrderPayload,
) => {
  const { data } = await api.post<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/send`, {
    ...payload,
    cc: PURCHASE_ORDER_SEND_CC_EMAILS,
    bcc: PURCHASE_ORDER_SEND_BCC_EMAILS,
  });

  return data;
};

export const receivePurchaseOrder = async (
  purchaseOrderId: string,
  payload: ReceivePurchaseOrderPayload,
) => {
  const { data } = await api.post<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/receive`, payload);

  return data;
};

export const updatePurchaseOrderCosts = async (
  purchaseOrderId: string,
  payload: UpdatePurchaseOrderCostsPayload,
) => {
  const { data } = await api.patch<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/costs`, payload);

  return data;
};

export const payPurchaseOrder = async (
  purchaseOrderId: string,
  payload: PayPurchaseOrderPayload,
) => {
  const { data } = await api.post<{
    message: string;
    item: PurchaseOrder;
  }>(`${URL}/purchase-order/${purchaseOrderId}/pay`, payload);

  return data;
};

export const deletePurchaseOrder = async (
  payload: DeletePurchaseOrderPayload,
) => {
  const { data } = await api.delete<{
    message: string;
  }>(`${URL}/purchase-order/delete`, {
    data: payload,
  });

  return data;
};
